import React, { useState, useEffect } from "react";
import { DateTime } from "luxon";
import "./calendar.css";
import Icon from '@mdi/react';
import { mdiChevronLeft, mdiChevronRight } from '@mdi/js';
import { getDanceStyleColor, resolveToId } from "../config/danceStyles";

// Function to extract dance style from event title
// eslint-disable-next-line no-unused-vars
const getDanceStyleFromTitle = title => {
    if (!title) return null;

    const titleLower = title.toLowerCase();

    // Check for each dance style in the title
    if (titleLower.includes("zouk")) return "zouk";
    if (titleLower.includes("bachata")) return "bachata";
    if (titleLower.includes("salsa")) return "salsa";
    if (titleLower.includes("tango")) return "tango";
    if (titleLower.includes("kizomba")) return "kizomba";
    if (titleLower.includes("forro")) return "forro";
    if (titleLower.includes("samba")) return "samba";
    if (titleLower.includes("lambada")) return "lambada";
    if (titleLower.includes("merengue")) return "merengue";
    if (titleLower.includes("west coast swing") || titleLower.includes("wcs")) return "west coast swing";

    // Default if no match found
    return null;
};

// Determine the colour for an event from its dance style (canonical config hex).
// Prefers the stored danceStyles[0] id; falls back to parsing the title.
// Unrecognised styles keep the neutral purple default used for calendar badges.
const getColorForEvent = event => {
    const candidate = (event.danceStyles && event.danceStyles[0]) || getDanceStyleFromTitle(event.title || event.name);
    const id = resolveToId(candidate);
    return id ? getDanceStyleColor(id) : "var(--dl-color-purple-default)";
};

// Phase 3: Define time periods matching mobile app
const TIME_PERIODS = [
    { name: "Morning", start: 7, end: 11 },
    { name: "Midday", start: 11, end: 14 },
    { name: "Afternoon", start: 14, end: 22 },
];

// Phase 3: Check if a slot falls within a time period
const isSlotInPeriod = (slot, period) => {
    try {
        // Parse start time (format: "HH:mm" like "09:00")
        const [hours] = slot.startTime.split(':').map(Number);
        return hours >= period.start && hours < period.end;
    } catch (error) {
        console.error("Error parsing slot time:", slot, error);
        return false;
    }
};

// Phase 3: Calculate availability state for a time period
// States: 'available', 'partial', 'booked', 'none'
const calculatePeriodState = (slots, period, bookings = []) => {
    // Filter slots that fall within this period
    const periodSlots = slots.filter(slot => isSlotInPeriod(slot, period));
    const totalSlots = periodSlots.length;

    if (totalSlots === 0) return 'none';

    // Phase 2: Calculate booked slots by checking bookings
    const bookedSlots = periodSlots.filter(slot => {
        // Check if this slot has a confirmed OR pending booking
        return bookings.some(booking => {
            // Match by date (compare date portion only, bookingDate may include time)
            const bookingDate = booking.bookingDate ? booking.bookingDate.split('T')[0] : '';
            const slotDate = slot.date ? slot.date.split('T')[0] : '';
            if (bookingDate !== slotDate) return false;

            // Check if booking time overlaps with slot time
            const bookingStart = booking.timeSlot?.startTime;
            // A slot is "taken" if it holds any live-or-served booking: pending/confirmed
            // (upcoming) OR completed (a past lesson that already happened). Excluding
            // completed made past days that held lessons show green "available" again.
            // ("paid" is not a booking.status — paid-ness lives in pricing.paymentStatus.)
            return bookingStart === slot.startTime &&
                ['pending', 'confirmed', 'completed'].includes(booking.status);
        });
    }).length;

    const availableSlots = totalSlots - bookedSlots;

    // Determine state based on available vs booked ratio
    if (availableSlots === totalSlots) return 'available'; // All slots available
    if (availableSlots === 0) return 'booked'; // Fully booked
    return 'partial'; // Some available, some booked
};

/**
 * A compact calendar view component that can be reused across the application
 * @param {Object} props
 * @param {Array} props.events - Array of event objects to display in the calendar
 * @param {boolean} props.compact - Whether to use a more compact display (default: true)
 * @param {boolean} props.availabilityMode - Phase 3: Show instructor availability instead of events (default: false)
 * @param {Array} props.availabilitySlots - Phase 3: Array of availability slot objects {date, startTime, endTime, locationId, status}
 * @param {Array} props.bookings - Phase 3: Array of booking objects for calculating slot states
 * @param {Function} props.onSlotClick - Phase 3: Callback when availability slot is clicked (day, slots)
 * @param {number} props.weekStartDay - Luxon weekday to start the week (1=Mon, 3=Wed, 7=Sun). Default: 3 (Wednesday)
 */
const CalendarView = ({
    events = [],
    compact = true,
    viewType = "monthly",
    onWeekChange,
    currentDate: propCurrentDate,
    onEventClick,
    // Optional events-mode override: when provided, a day-cell click calls this
    // (day, dayEvents) instead of opening CalendarView's built-in day-detail modal.
    // Lets a parent route clicks straight to an event / a styled chooser.
    onDayClick,
    isMobile = false,
    // Phase 3: Availability mode props
    availabilityMode = false,
    availabilitySlots = [],
    bookings = [],
    onSlotClick,
    selectedDateProp, // External selected date for highlighting (ISO string)
    weekStartDay = 3,
    // Phase 3: View mode toggle props
    showViewToggle = false,
    viewMode = 'availability',
    onViewModeChange,
    travelPeriods = [],
    blockedDates = [],
    sickDays = [],
    // Month navigation callbacks
    onPreviousMonth,
    onNextMonth,
    // Mini calendar mode - CSS aspect-ratio sizing like mobile
    miniMode = false,
    // Disable past date selection
    disablePastDates = false,
}) => {
    const [currentDate, setCurrentDate] = useState(DateTime.local());
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedDayEvents, setSelectedDayEvents] = useState([]);
    const [selectedDate, setSelectedDate] = useState(null);

    // Calculate CSS classes based on compact mode, mobile, and mini mode
    const containerClass = `calendar-container ${compact ? "compact" : ""} ${isMobile ? "mobile-wide" : ""}`.trim();
    const gridClass = `calendar-grid ${compact ? "compact" : "normal"} ${isMobile ? "mobile-wide" : ""} ${miniMode ? "mini-mode" : ""}`.trim();
    const dayClass = `calendar-day ${compact ? "compact" : "normal"} ${isMobile ? "mobile-wide" : ""} ${miniMode ? "mini-mode" : ""}`.trim();
    const eventsClass = compact ? "calendar-events-container compact" : "calendar-events-container normal";

    // Update local state when prop changes
    useEffect(() => {
        if (propCurrentDate) {
            setCurrentDate(propCurrentDate);
        }
    }, [propCurrentDate]);

    const getDaysInMonth = () => {
        const firstDayOfMonth = currentDate.startOf("month");
        const lastDayOfMonth = currentDate.endOf("month");

        // Find the first occurrence of weekStartDay before the start of the month
        let startDate = firstDayOfMonth;
        while (startDate.weekday !== weekStartDay) {
            startDate = startDate.minus({ days: 1 });
        }

        const days = [];
        let currentDay = startDate;

        while (currentDay <= lastDayOfMonth || days.length % 7 !== 0) {
            days.push(currentDay);
            currentDay = currentDay.plus({ days: 1 });
        }

        return days;
    };

    const getEventsForDay = day => {
        if (!events || events.length === 0) return [];

        return events.filter(event => {
            try {
                let startDate;

                // Parse start date - Backend returns plain ISO strings (not $date wrapper)
                if (typeof event.eventStartDateTime === "string") {
                    startDate = DateTime.fromISO(event.eventStartDateTime);
                } else {
                    startDate = DateTime.fromJSDate(new Date(event.eventStartDateTime));
                }

                // Check if the current day matches the event's start date
                const dayStart = day.startOf("day");
                const eventStart = startDate.startOf("day");

                // Event is shown only on its start day to prevent duplicates
                return dayStart.equals(eventStart);
            } catch (error) {
                console.error("Error parsing date for event:", event.title, error);
                return false;
            }
        });
    };

    // Phase 3: Get availability slots for a specific day
    // CRITICAL: If a travel period exists for this day, show ONLY travel slots (hide home schedule)
    const getAvailabilitySlotsForDay = day => {
        if (!availabilitySlots || availabilitySlots.length === 0) return [];

        // First, filter slots by date
        const daySlots = availabilitySlots.filter(slot => {
            try {
                // Parse slot date
                const slotDate = DateTime.fromISO(slot.date);
                const dayStart = day.startOf("day");

                // Check if slot matches this day
                return dayStart.equals(slotDate.startOf("day"));
            } catch (error) {
                console.error("Error parsing availability slot date:", slot, error);
                return false;
            }
        });

        // Check if this day is within a travel period
        const dayTravelPeriods = getTravelPeriodsForDay(day);
        const hasTravelPeriod = dayTravelPeriods.length > 0;

        if (hasTravelPeriod) {
            // Day is in travel period - show ONLY travel slots (filter out home schedule)
            return daySlots.filter(slot => slot.location?.type === 'travel');
        }

        // No travel period - show home schedule (filter out any travel slots)
        return daySlots.filter(slot => slot.location?.type !== 'travel');
    };

    // Helper: Get visible travel periods for current month
    const getVisibleTravelPeriods = () => {
        if (!travelPeriods || travelPeriods.length === 0 || !currentDate) return [];

        const monthStart = currentDate.startOf("month");
        const monthEnd = currentDate.endOf("month");

        // Filter periods that overlap with current month
        return travelPeriods.filter(period => {
            try {
                const periodStart = DateTime.fromISO(period.startDate);
                const periodEnd = DateTime.fromISO(period.endDate);
                return periodStart <= monthEnd && periodEnd >= monthStart;
            } catch (error) {
                return false;
            }
        });
    };

    // Helper: Get travel periods that include this day (from visible periods)
    const getTravelPeriodsForDay = (day) => {
        const visiblePeriods = getVisibleTravelPeriods();
        if (visiblePeriods.length === 0) return [];

        return visiblePeriods.filter(period => {
            try {
                const start = DateTime.fromISO(period.startDate).startOf('day');
                const end = DateTime.fromISO(period.endDate).startOf('day');
                const dayStart = day.startOf('day');
                return dayStart >= start && dayStart <= end;
            } catch (error) {
                console.error('Error checking travel period for day:', error);
                return false;
            }
        });
    };

    // Helper: Get border color for travel period
    // Backend assigns colour from TRAVEL_COLORS during context creation;
    // every travel period has one. No hex fallback — a missing colour
    // should surface as an obvious "no border" bug, not be masked.
    const getTravelBorderColor = (dayTravelPeriods) => {
        if (dayTravelPeriods.length === 0) return null;
        return dayTravelPeriods[0].color;
    };

    // Helper: Get blocked date context for this day
    const getBlockedDateContext = (day) => {
        if (!blockedDates || blockedDates.length === 0) return null;
        const dayStart = day.startOf('day');
        return blockedDates.find(ctx => {
            try {
                const s = DateTime.fromISO(ctx.startDate).startOf('day');
                const e = DateTime.fromISO(ctx.endDate).startOf('day');
                return dayStart >= s && dayStart <= e;
            } catch (_) {
                return false;
            }
        }) || null;
    };

    // Helper: Get sick day override for this day
    const getSickDayForDate = (day) => {
        if (!sickDays || sickDays.length === 0) return null;
        const dateStr = day.toISODate();
        return sickDays.find(s => s.date === dateStr) || null;
    };

    const handleDayClick = (day, dayEvents) => {
        if (dayEvents.length > 0) {
            setSelectedDate(day);
            setSelectedDayEvents(dayEvents);
            setModalVisible(true);
        }
    };

    return (
        <div className={containerClass}>
            {/* Month Navigation Header - Full-width layout: Prev (left) | Title (center) | Next (right) */}
            {availabilityMode && (
                <div className="calendar-month-nav-header">
                    <button
                        onClick={() => {
                            const newDate = currentDate.minus({ months: 1 });

                            // Don't allow navigating to past months if disablePastDates is true
                            if (disablePastDates) {
                                const today = DateTime.local().startOf('month');
                                if (newDate < today) {
                                    return; // Block navigation to past months
                                }
                            }

                            setCurrentDate(newDate);
                            if (onPreviousMonth) onPreviousMonth(newDate);
                        }}
                        disabled={disablePastDates && currentDate.startOf('month') <= DateTime.local().startOf('month')}
                        className="calendar-month-nav-btn"
                        aria-label="Previous month"
                    >
                        <Icon path={mdiChevronLeft} size={1} />
                    </button>

                    <h2 className="calendar-month-title">
                        {currentDate.toFormat("MMMM yyyy")}
                    </h2>

                    <button
                        onClick={() => {
                            const newDate = currentDate.plus({ months: 1 });
                            setCurrentDate(newDate);
                            if (onNextMonth) onNextMonth(newDate);
                        }}
                        className="calendar-month-nav-btn"
                        aria-label="Next month"
                    >
                        <Icon path={mdiChevronRight} size={1} />
                    </button>
                </div>
            )}

            {/* For non-availability mode (events mode): centered month/range title with chevron nav */}
            {!availabilityMode && (
                <div className="calendar-month-nav-header">
                    <button
                        onClick={() => {
                            if (viewType === "weekly") {
                                const newDate = currentDate.minus({ weeks: 1 });
                                if (disablePastDates && newDate.endOf("week") < DateTime.local().startOf("day")) {
                                    return; // Block navigation to fully-past weeks
                                }
                                setCurrentDate(newDate);
                                if (onWeekChange) onWeekChange(newDate);
                            } else {
                                const newDate = currentDate.minus({ months: 1 });
                                if (disablePastDates && newDate < DateTime.local().startOf("month")) {
                                    return; // Block navigation to past months
                                }
                                setCurrentDate(newDate);
                                if (onPreviousMonth) onPreviousMonth(newDate);
                            }
                        }}
                        disabled={
                            disablePastDates &&
                            (viewType === "weekly"
                                ? currentDate.startOf("week") <= DateTime.local().startOf("week")
                                : currentDate.startOf("month") <= DateTime.local().startOf("month"))
                        }
                        className="calendar-month-nav-btn"
                        aria-label={viewType === "weekly" ? "Previous week" : "Previous month"}>
                        <Icon path={mdiChevronLeft} size={1} />
                    </button>

                    <h2 className="calendar-month-title">
                        {currentDate.toFormat("MMMM yyyy")}
                    </h2>

                    <button
                        onClick={() => {
                            if (viewType === "weekly") {
                                const newDate = currentDate.plus({ weeks: 1 });
                                setCurrentDate(newDate);
                                if (onWeekChange) onWeekChange(newDate);
                            } else {
                                const newDate = currentDate.plus({ months: 1 });
                                setCurrentDate(newDate);
                                if (onNextMonth) onNextMonth(newDate);
                            }
                        }}
                        className="calendar-month-nav-btn"
                        aria-label={viewType === "weekly" ? "Next week" : "Next month"}>
                        <Icon path={mdiChevronRight} size={1} />
                    </button>
                </div>
            )}

            <div className={gridClass}>
                {(() => {
                    // Generate weekday labels based on weekStartDay
                    const allDays = ["Mon", "Tue", "Wed", "Thu", "Fri", "SAT", "SUN"];
                    const startIndex = weekStartDay - 1; // Luxon uses 1-based, array is 0-based
                    const rotatedDays = [...allDays.slice(startIndex), ...allDays.slice(0, startIndex)];

                    return rotatedDays.map((day, index) => {
                        // Calculate actual Luxon weekday for this position
                        const actualWeekday = ((weekStartDay - 1 + index) % 7) + 1;
                        // Weekend is Saturday (6) or Sunday (7)
                        const isWeekend = actualWeekday === 6 || actualWeekday === 7;

                        return (
                            <div key={day} className={`calendar-weekday ${isWeekend ? 'weekend' : ''}`}>
                                {day}
                            </div>
                        );
                    });
                })()}

                {(() => {
                    const allDays = getDaysInMonth();
                    const daysToRender =
                        viewType === "weekly"
                            ? (() => {
                                  // Find the week that contains currentDate
                                  for (let i = 0; i < allDays.length; i += 7) {
                                      const week = allDays.slice(i, i + 7);
                                      if (week.some(day => day.hasSame(currentDate, "day"))) {
                                          return week;
                                      }
                                  }
                                  return allDays.slice(0, 7); // fallback to first week
                              })()
                            : allDays;

                    return daysToRender.map((day, index) => {
                        const dayEvents = getEventsForDay(day);
                        const daySlots = availabilityMode ? getAvailabilitySlotsForDay(day) : [];
                        const isToday = day.hasSame(DateTime.local(), "day");
                        const isCurrentMonth = day.month === currentDate.month;
                        const hasAvailability = availabilityMode && daySlots.length > 0;
                        const isSelectedDay = selectedDateProp && day.toISODate() === selectedDateProp;
                        const isPastDay = day.startOf('day') < DateTime.local().startOf('day');
                        // Fully-disabled past day (blocks selection). When disablePastDates is
                        // false the day stays selectable but still reads as muted/receded via
                        // .past-date-muted — the instructor can click into a past date to reach
                        // its old bookings (e.g. to Request Payment) without it looking active.
                        const isPastDisabled = disablePastDates && isPastDay;
                        const isPastMuted = isPastDay && isCurrentMonth && !isPastDisabled;

                        // Check if day is a weekend (Saturday=6 or Sunday=7 in Luxon)
                        const isWeekendDay = day.weekday === 6 || day.weekday === 7;

                        // Check if day is in travel period
                        const dayTravelPeriods = getTravelPeriodsForDay(day);
                        const travelBorderColor = getTravelBorderColor(dayTravelPeriods);

                        // Check if day is within a blocked date context
                        const blockedCtx = isCurrentMonth ? getBlockedDateContext(day) : null;
                        const blockBorderColor = blockedCtx ? blockedCtx.color : null;

                        // Check if day has a sick day override (only when no travel or block ctx)
                        const sickDay = (isCurrentMonth && !travelBorderColor && !blockBorderColor)
                            ? getSickDayForDate(day)
                            : null;
                        const sickBorderColor = sickDay ? true : null;

                        return (
                            <div
                                key={day.toISODate()}
                                onClick={() => {
                                    // Block clicks on days from other months (overhang days)
                                    if (!isCurrentMonth) {
                                        return; // Don't allow clicking overhang days
                                    }

                                    // Disable past dates if prop enabled
                                    if (disablePastDates) {
                                        const today = DateTime.local().startOf('day');
                                        if (day.startOf('day') < today) {
                                            return; // Block past dates
                                        }
                                    }

                                    if (!availabilityMode) {
                                        // Events mode: let the parent override (route
                                        // straight to an event / styled chooser), else
                                        // fall back to the built-in day-detail modal.
                                        if (onDayClick) {
                                            onDayClick(day, dayEvents);
                                        } else {
                                            handleDayClick(day, dayEvents);
                                        }
                                    } else if (availabilityMode && onSlotClick) {
                                        // Availability mode: Call onSlotClick handler if provided
                                        onSlotClick(day.toISODate(), daySlots);
                                    }
                                }}
                                className={`
                                    ${dayClass}
                                    ${viewType === "weekly" ? "weekly" : ""}
                                    ${isCurrentMonth ? "" : "other-month"}
                                    ${isToday ? "today" : ""}
                                    ${hasAvailability ? "has-availability" : ""}
                                    ${isSelectedDay ? "selected-day" : ""}
                                    ${isPastDisabled ? "past-date" : ""}
                                    ${isPastMuted ? "past-date-muted" : ""}
                                `}
                                style={{
                                    // Cursor: not-allowed for other-month days and disabled past dates, pointer for clickable, default otherwise
                                    cursor: !isCurrentMonth
                                        ? 'not-allowed'
                                        : isPastDisabled
                                            ? 'not-allowed'
                                            : (availabilityMode && onSlotClick) ? 'pointer' : 'default',
                                    // Blocked date context coloring (highest priority — overrides travel)
                                    ...(blockBorderColor && {
                                        border: `2px solid ${blockBorderColor}`,
                                        boxShadow: `0 0 0 1px ${blockBorderColor}40`,
                                        backgroundColor: `${blockBorderColor}15`
                                    }),
                                    // Travel period coloring (only when no blocked date)
                                    ...(!blockBorderColor && travelBorderColor && {
                                        border: `2px solid ${travelBorderColor}`,
                                        boxShadow: `0 0 0 1px ${travelBorderColor}40`,
                                        backgroundColor: `${travelBorderColor}20`
                                    }),
                                    // Sick day coloring (lowest priority)
                                    ...(!blockBorderColor && !travelBorderColor && sickBorderColor && {
                                        border: `2px solid var(--dl-color-sick-red)`,
                                        boxShadow: `0 0 0 1px color-mix(in srgb, var(--dl-color-sick-red) 25%, transparent)`,
                                        backgroundColor: `color-mix(in srgb, var(--dl-color-sick-red) 7%, transparent)`
                                    })
                                }}>

                                <div className={`calendar-day-number ${isCurrentMonth ? "current-month" : "other-month"} ${isWeekendDay ? "weekend" : ""}`}>
                                    <span className="calendar-day-number-badge">{day.day}</span>
                                </div>

                                {/* Events mode: Show event cards — each bar is individually
                                    clickable and routes to its own event (stopPropagation so it
                                    doesn't also trigger the day-cell handler). */}
                                {!availabilityMode && (
                                    <div className={eventsClass}>
                                        {dayEvents.map((event, eventIndex) => (
                                            <div
                                                key={`${event.id}-${eventIndex}`}
                                                className={`calendar-event ${onEventClick ? "clickable" : ""}`}
                                                style={{ backgroundColor: getColorForEvent(event) }}
                                                title={event.title}
                                                onClick={onEventClick ? (e) => {
                                                    e.stopPropagation();
                                                    onEventClick(event);
                                                } : undefined}>
                                                {event.title}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Blocked date text overlay — sits above badges */}
                                {availabilityMode && blockBorderColor && (
                                    <div
                                        className="calendar-day-blocked-label"
                                        style={{ color: blockBorderColor }}
                                    >
                                        {{ holiday: 'Pub.Hol', leave: 'Annual', personal: 'Personal' }[blockedCtx?.context] || 'Blocked'}
                                    </div>
                                )}

                                {/* Sick day text overlay */}
                                {availabilityMode && sickBorderColor && (
                                    <div className="calendar-day-sick-label">
                                        Sick Day
                                    </div>
                                )}

                                {/* Availability mode: Show 4-slot availability badges */}
                                {availabilityMode && (
                                    <div className="availability-badges">
                                        {TIME_PERIODS.map((period) => {
                                            const state = calculatePeriodState(daySlots, period, bookings);

                                            return (
                                                <div
                                                    key={period.name}
                                                    className={`availability-badge availability-badge--${state}`}
                                                    title={`${period.name}: ${state}`}
                                                >
                                                    {state === 'none' ? '-' : period.name}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    });
                })()}
            </div>
            {modalVisible && (
                <div className="calendar-modal-overlay">
                    <div className="calendar-modal-content">
                        <div className="calendar-modal-header">
                            <h3 className="calendar-modal-title">
                                Events for {selectedDate?.toFormat("EEEE, MMMM d")}
                            </h3>
                        </div>
                        <div className="calendar-modal-body">
                            {selectedDayEvents.map((event, index) => (
                                <div key={event.id || index} className="calendar-event-detail">
                                    <h4 className="calendar-event-detail-title">{event.title}</h4>
                                    <div className="calendar-event-detail-info">
                                        <div className="calendar-event-detail-info-row">
                                            <span className="calendar-event-detail-label">Time:</span>
                                            {(() => {
                                                try {
                                                    // Backend returns plain ISO strings (not $date wrapper)
                                                    let eventDate;
                                                    if (typeof event.eventStartDateTime === "string") {
                                                        eventDate = DateTime.fromISO(event.eventStartDateTime);
                                                    } else {
                                                        eventDate = DateTime.fromJSDate(
                                                            new Date(event.eventStartDateTime)
                                                        );
                                                    }
                                                    return eventDate.toFormat("h:mm a");
                                                } catch (error) {
                                                    return "Time TBA";
                                                }
                                            })()}
                                        </div>
                                        {event.eventLocation?.description && (
                                            <div className="calendar-event-detail-info-row">
                                                <span className="calendar-event-detail-label">Location:</span>
                                                {event.eventLocation.description}
                                            </div>
                                        )}
                                        {event.danceStyles && (
                                            <div className="calendar-event-detail-info-row">
                                                <span className="calendar-event-detail-label">Styles:</span>
                                                {event.danceStyles.join(", ")}
                                            </div>
                                        )}
                                        <div className="calendar-event-detail-actions">
                                            {onEventClick && (
                                                <button
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        onEventClick(event);
                                                        setModalVisible(false);
                                                    }}
                                                    className="calendar-details-btn"
                                                    title="View Event Details">
                                                    View Details
                                                </button>
                                            )}
                                            {event.facebookUrl && (
                                                <button
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        window.open(event.facebookUrl, "_blank");
                                                    }}
                                                    className="calendar-facebook-btn"
                                                    title="View on Facebook">
                                                    <svg className="calendar-facebook-icon" fill="currentColor" viewBox="0 0 24 24">
                                                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                                                    </svg>
                                                    View on Facebook
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="calendar-modal-footer">
                            <button
                                onClick={() => setModalVisible(false)}
                                className="calendar-modal-close-btn">
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CalendarView;
